"use client";

import { useState, useEffect, useCallback, useRef, useMemo, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { type DomainId, DOMAINS } from "@/data/domains";
import { useMotionMode } from "@/context/MotionContext";
import { useThemeMode } from "@/context/ThemeContext";
import { useAudio } from "@/context/AudioContext";
import {
  SunIcon,
  MoonIcon,
  CRTIcon,
  CRTOffIcon,
  SpeakerOnIcon,
  SpeakerOffIcon,
  CopyIcon,
  ExternalLinkIcon,
  ArrowRightIcon,
  ICON_SIZE_SM,
} from "@/components/ui/Button";

// =============================================================================
// COMMAND PALETTE
// =============================================================================
// Cmd+K / Ctrl+K searchable palette for domain navigation + quick actions.
// Auto-populates domains from DOMAINS array.

type PaletteItem =
  | { type: "domain"; id: string; label: string; color: string; section: "Navigation" }
  | { type: "action"; id: string; label: string; icon: ReactNode; execute: () => void; section: "Actions" };

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  activeDomain: DomainId;
  onNavigate: (target: DomainId) => void;
  crtEnabled: boolean;
  onToggleCRT: () => void;
}

export function CommandPalette({
  isOpen,
  onClose,
  activeDomain,
  onNavigate,
  crtEnabled,
  onToggleCRT,
}: CommandPaletteProps) {
  // Consume contexts directly (same pattern as ToggleTray)
  const { mode: themeMode, toggleMode } = useThemeMode();
  const { isMuted, setMuted } = useAudio();
  const isLightMode = themeMode === "light";
  const { canAnimate } = useMotionMode();
  const [mounted, setMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Build palette items from DOMAINS + actions
  const allItems: PaletteItem[] = useMemo(() => {
    const domainItems: PaletteItem[] = DOMAINS.map((d) => ({
      type: "domain" as const,
      id: d.id,
      label: d.label,
      color: d.color,
      section: "Navigation" as const,
    }));

    const actionItems: PaletteItem[] = [
      {
        type: "action",
        id: "toggle-theme",
        label: isLightMode ? "Switch to dark mode" : "Switch to light mode",
        icon: isLightMode ? <MoonIcon size={ICON_SIZE_SM} /> : <SunIcon size={ICON_SIZE_SM} />,
        execute: toggleMode,
        section: "Actions",
      },
      {
        type: "action",
        id: "toggle-crt",
        label: crtEnabled ? "Turn CRT effects off" : "Turn CRT effects on",
        icon: crtEnabled ? <CRTOffIcon size={ICON_SIZE_SM} /> : <CRTIcon size={ICON_SIZE_SM} />,
        execute: onToggleCRT,
        section: "Actions",
      },
      {
        type: "action",
        id: "toggle-audio",
        label: isMuted ? "Turn audio on" : "Turn audio off",
        icon: isMuted ? <SpeakerOffIcon size={ICON_SIZE_SM} /> : <SpeakerOnIcon size={ICON_SIZE_SM} />,
        execute: () => setMuted(!isMuted),
        section: "Actions",
      },
      {
        type: "action",
        id: "copy-email",
        label: "Copy email address",
        icon: <CopyIcon size={ICON_SIZE_SM} />,
        execute: () => {
          navigator.clipboard.writeText("hello@vincentngu.com");
        },
        section: "Actions",
      },
      {
        type: "action",
        id: "open-resume",
        label: "Open resume",
        icon: <ExternalLinkIcon size={ICON_SIZE_SM} />,
        execute: () => {
          window.open("/resume.pdf", "_blank");
        },
        section: "Actions",
      },
    ];

    return [...domainItems, ...actionItems];
  }, [isLightMode, crtEnabled, isMuted, toggleMode, onToggleCRT, setMuted]);

  // Filter by query
  const filteredItems = useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.toLowerCase();
    return allItems.filter((item) => item.label.toLowerCase().includes(q));
  }, [allItems, query]);

  // Reset highlight when filter changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredItems.length]);

  // Open/close visibility
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      setIsVisible(true);
      setQuery("");
      setHighlightedIndex(0);
      const timer = setTimeout(() => {
        setShowContent(true);
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    } else {
      setShowContent(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Restore focus on close
  useEffect(() => {
    if (!isOpen && !isVisible) {
      previousFocusRef.current?.focus();
    }
  }, [isOpen, isVisible]);

  // Execute the highlighted item
  const executeItem = useCallback(
    (item: PaletteItem) => {
      if (item.type === "domain") {
        onNavigate(item.id as DomainId);
      } else {
        item.execute();
        onClose();
      }
    },
    [onNavigate, onClose]
  );

  // Keyboard nav inside the palette
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          filteredItems.length > 0
            ? (prev + 1) % filteredItems.length
            : 0
        );
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          filteredItems.length > 0
            ? (prev - 1 + filteredItems.length) % filteredItems.length
            : 0
        );
        return;
      }

      if (e.key === "Enter" && filteredItems.length > 0) {
        e.preventDefault();
        executeItem(filteredItems[highlightedIndex]);
        return;
      }
    },
    [onClose, filteredItems, highlightedIndex, executeItem]
  );

  useEffect(() => {
    if (!isVisible) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, handleKeyDown]);

  if (!mounted || !isVisible) return null;

  // Group filtered items by section for display
  const sections = new Map<string, PaletteItem[]>();
  for (const item of filteredItems) {
    const group = sections.get(item.section) ?? [];
    group.push(item);
    sections.set(item.section, group);
  }

  // Flat index mapping for highlight tracking
  let flatIndex = 0;

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      data-lenis-prevent
      className="fixed inset-0 z-[275] flex items-start justify-center"
      onClick={onClose}
      style={{
        paddingTop: "min(20vh, 160px)",
        backgroundColor: isLightMode
          ? "rgba(240,240,240,0.85)"
          : "rgba(0,0,0,0.8)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        opacity: showContent ? 1 : 0,
        transition: canAnimate ? "opacity 150ms ease-out" : "none",
      }}
    >
      {/* Panel */}
      <div
        className="w-full rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 480,
          margin: "0 16px",
          backgroundColor: isLightMode
            ? "rgba(255,255,255,0.9)"
            : "rgba(30,30,30,0.95)",
          border: `1px solid ${isLightMode ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)"}`,
          boxShadow: isLightMode
            ? "0 16px 48px rgba(0,0,0,0.15)"
            : "0 16px 48px rgba(0,0,0,0.5)",
          transform: showContent ? "translateY(0)" : "translateY(-12px)",
          opacity: showContent ? 1 : 0,
          transition: canAnimate
            ? "transform 200ms cubic-bezier(0.16, 1, 0.3, 1), opacity 150ms ease-out"
            : "none",
        }}
      >
        {/* Search input */}
        <div
          className="flex items-center gap-3 px-4 py-3"
          style={{
            borderBottom: `1px solid ${isLightMode ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)"}`,
          }}
        >
          {/* Search icon */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{ flexShrink: 0, opacity: 0.4 }}
          >
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type a command..."
            className="flex-1 bg-transparent border-none outline-none text-sm font-mono"
            style={{
              color: `rgba(var(--foreground-rgb), 0.9)`,
              caretColor: `rgba(var(--foreground-rgb), 0.5)`,
            }}
            aria-label="Search commands"
          />
          {/* Shortcut badge */}
          <kbd
            className="text-[10px] font-mono px-1.5 py-0.5 rounded select-none"
            style={{
              backgroundColor: isLightMode
                ? "rgba(0,0,0,0.06)"
                : "rgba(255,255,255,0.06)",
              color: `rgba(var(--foreground-rgb), 0.3)`,
            }}
          >
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[70vh] overflow-y-auto overscroll-contain py-1">
          {filteredItems.length === 0 ? (
            <div
              className="px-4 py-6 text-center text-sm"
              style={{ color: `rgba(var(--foreground-rgb), 0.3)` }}
            >
              No results
            </div>
          ) : (
            Array.from(sections.entries()).map(([sectionName, items]) => (
              <div key={sectionName}>
                {/* Section header */}
                <div
                  className="px-4 pt-3 pb-1 text-[10px] font-mono font-bold uppercase tracking-widest select-none"
                  style={{ color: `rgba(var(--foreground-rgb), 0.3)` }}
                >
                  {sectionName}
                </div>
                {/* Items */}
                {items.map((item) => {
                  const thisIndex = flatIndex++;
                  const isHighlighted = thisIndex === highlightedIndex;
                  const isDomainActive =
                    item.type === "domain" && item.id === activeDomain;

                  return (
                    <button
                      key={item.id}
                      onClick={() => executeItem(item)}
                      onMouseEnter={() => setHighlightedIndex(thisIndex)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-left cursor-pointer bg-transparent border-none outline-none"
                      style={{
                        backgroundColor: isHighlighted
                          ? isLightMode
                            ? "rgba(0,0,0,0.04)"
                            : "rgba(255,255,255,0.06)"
                          : "transparent",
                        transition: canAnimate
                          ? "background-color 100ms ease"
                          : "none",
                      }}
                      aria-current={isDomainActive ? "page" : undefined}
                    >
                      {/* Icon / domain dot */}
                      {item.type === "domain" ? (
                        <div
                          className="rounded-full flex-shrink-0"
                          style={{
                            width: 10,
                            height: 10,
                            backgroundColor: item.color,
                            opacity: isDomainActive ? 1 : 0.6,
                            boxShadow: isDomainActive
                              ? `0 0 8px ${item.color}60`
                              : "none",
                          }}
                        />
                      ) : (
                        <span
                          className="flex-shrink-0"
                          style={{
                            color: `rgba(var(--foreground-rgb), 0.5)`,
                            width: 14,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          {item.icon}
                        </span>
                      )}

                      {/* Label */}
                      <span
                        className="flex-1 text-sm"
                        style={{
                          color: `rgba(var(--foreground-rgb), ${isHighlighted ? 0.9 : 0.6})`,
                        }}
                      >
                        {item.label}
                      </span>

                      {/* Active / arrow indicator */}
                      {item.type === "domain" && isDomainActive && (
                        <span
                          className="text-[10px] uppercase tracking-wider"
                          style={{
                            color: item.color,
                            opacity: 0.7,
                          }}
                        >
                          here
                        </span>
                      )}
                      {item.type === "domain" && !isDomainActive && isHighlighted && (
                        <span style={{ color: `rgba(var(--foreground-rgb), 0.3)` }}>
                          <ArrowRightIcon size={12} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div
          className="flex items-center justify-center gap-3 px-4 py-2 select-none"
          style={{
            borderTop: `1px solid ${isLightMode ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)"}`,
            color: `rgba(var(--foreground-rgb), 0.25)`,
          }}
        >
          <span className="text-[10px] font-mono">↑↓ navigate</span>
          <span className="text-[10px] font-mono">↵ select</span>
          <span className="text-[10px] font-mono">esc close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
